// ============================================================================
// ORDERS · portal-local UI store
// ============================================================================
// Deliberately NOT a slice of the global uiStore: this state is meaningful only
// inside the Orders portal, and uiStore already sits at its 300-line cap
// (Convention #6). The Projects portal set the precedent with
// src/portals/projects/projectsUiStore.js — Orders follows it.
//
// `ordersView` is the active section. The in-portal pill row is its only writer
// today; if the AppHeader Sections dropdown ever drives Orders too, it writes
// THIS slice rather than forking a second one (Convention #141).
//
// B1a — the two worlds. `world` is null until the person chooses (the entry
// chooser renders in its place); 'book' is everything that exists today, 'desk'
// is Phase B2 and cannot be entered yet. The Book's year / sub-collection rail
// lives here too, so Overview and Table read ONE axis (Convention #141) — a
// null year means "the latest year that has rows", resolved by useBookScope.
// `pendingLine` is the deep-link hand-off: a notification or a ?line= address
// sets it, the Table consumes it once the rows are in, then clears it.
//
// The view keys (summary · builder · data · insights) are the rename-forbidden
// store values from the B0 Discovery §E; the URL wears different words
// (overview · builder · table · insights) and useOrdersUrl maps between them.
// ============================================================================

import { create } from 'zustand';
import { BOOK_KINDS } from './model/bookScope';
import { BUCKETS as EXCEPTION_BUCKET_LIST, DEFAULT_BUCKET as DEFAULT_EXCEPTION_BUCKET } from './collection/exceptionsModel';
import { cleanPicks } from './collection/collectionFacets';

export const ORDERS_VIEWS = ['summary', 'builder', 'data', 'insights', 'compare'];   // CLB-064 · + Compare years
export const DEFAULT_ORDERS_VIEW = 'summary';
export const ORDERS_WORLDS = ['book', 'desk', 'collection'];
export const BOOK_SUBS = ['all', 'main', 'pre', 'distributors', 'thailand', 'aw'];

// B2a — the Desk's own axis. DESK_NAV is what the approved prototype's nav
// shows, in its order; `new` and `card` are reachable states rather than nav
// entries (a primary button and a click on a row). B5-1 c4: the B5 prototype
// (docs/prototypes/2026-09-14-orders-b5-today-proto-v1.html) carries FIVE —
// Today · Orders · PI Inbox · Suppliers · Settings; Deliveries and Data left
// the bar (deliveries live on Today and the card; Data is a later phase).
// Suppliers still renders the one-line panel naming the phase that brings it.
// ORD-BANKS-6 · + Banks (Settings → Banks): the four controlled banks, reviewed by the Orders admins.
// ORD-SHEETS-1 · + Sheets (key `print`): the printable meeting forms. Not `sheets` — that word
// already names the meeting-sheet batch reader (desk/sheets/, `batch`, &sheet=).
// ORD-NAMES-2 · + Names: the model-name bank. Not drawn by DeskShell — names/useNamesRoute.js opens the Names department.
export const DESK_NAV = ['today', 'models', 'reminders', 'orders', 'print', 'inbox', 'suppliers', 'settings', 'banks', 'access', 'names'];
export const DESK_VIEWS = [...DESK_NAV, 'new', 'card', 'batch', 'followup', 'health', 'export', 'datacenter', 'items', 'model'];   // B2b · batch: a pile of meeting sheets · CLB-115 S9a · followup: the Follow-up Centre · S9d · health: Archive & health · S9e · export: Export Studio · S9f · datacenter: Data center · S4c · items: Items · S4d · model: the model page (&model=)
export const DEFAULT_DESK_VIEW = 'orders';

// C4 — the Collection world's own axis. Six tabs from the approved prototype
// (its Dev board tab is dropped — Part 2 lives in the Developments portal since
// C2); `wall` is the default. The toolbar's controls are ONE slice here so the
// address bar can mirror them and a pasted link reproduces a view (Conv #141):
// q · status (multi) · group · sort · density · model (the open drawer's code).
// The advanced facets (supplier · designer · material · shape · flags) sit in
// the same slice but stay off the URL — a Filters popover, not an address.
// C8 · the Wall's control deck picks (fam · mats · subs · nyo) DO ride the
// address (&fam= &mat= &sub= &nyo=1, collectionFacets' codec).
// Density is also remembered per viewer in localStorage (a convenience, never
// a source of truth — the store is).
export const COLLECTION_VIEWS = ['wall', 'table', 'overview', 'repeats', 'compare', 'exceptions', 'email'];   // CLB-069 · + Email Studio (staff only)
export const DEFAULT_COLLECTION_VIEW = 'wall';
export const COLLECTION_STATUSES = ['await', 'pre', 'ord', 'man', 'exc', 'plan', 'rev'];   // C7·2b · plan · rev are the repeats' own
// C7·2b · the Wall's kind switch (All · Repeats · New collection); the Table keeps its own (C8).
export const WALL_KIND_KEYS = ['all', 'rep', 'new'];
// C8 · the Wall's group menu adds sub (Sub-shape) and material and starts at none; its sort adds colours.
export const COLLECTION_GROUPS = ['none', 'status', 'supplier', 'designer', 'shape', 'stage', 'sub', 'material'];
export const COLLECTION_SORTS = ['code', 'pieces', 'amount', 'base', 'supplier', 'newest', 'colours'];
export const COLLECTION_DENSITIES = ['compact', 'regular'];
export const COLLECTION_FACETS = ['supplier', 'designer', 'material', 'shape', 'flags'];
// C6·3 · the Repeats tab's own toolbar (its own words): the status seg, the
// eleven-column sort with its direction, and the popover's seven facet groups.
export const REPEAT_STATUSES = ['all', 'ordered', 'planned', 'revived'];
export const REPEAT_SORTS = ['code', 'type', 'material', 'shape', 'base', 'planned', 'ordered', 'status', 'units', 'gap'];
export const REPEAT_FACETS = ['status', 'type', 'material', 'shape', 'sub', 'supplier', 'flag'];
const EMPTY_RFACETS = Object.fromEntries(REPEAT_FACETS.map((k) => [k, []]));
// C7 · the Exceptions tab: the open bucket (xb) and its search (xq) — both on the address.
export const EXCEPTION_BUCKETS = EXCEPTION_BUCKET_LIST.map((b) => b.k);
const DENSITY_KEY = 'clb.collection.density';
// C6·3 · the Repeats tab's density is its OWN (rdensity): it scales the photo/tile tokens only and
// never the world's chrome, so switching it can never move the sub-header, tabs or the Wall.
const RDENSITY_KEY = 'clb.collection.rdensity';
const readDensity = (key = DENSITY_KEY) => { try { const d = localStorage.getItem(key); return COLLECTION_DENSITIES.includes(d) ? d : 'compact'; } catch (_) { return 'compact'; } };
const rememberDensity = (d, key = DENSITY_KEY) => { try { localStorage.setItem(key, d); } catch (_) { /* a private window is fine */ } };
const EMPTY_FACETS = Object.fromEntries(COLLECTION_FACETS.map((k) => [k, []]));
const pick = (list, v, fallback) => (list.includes(v) ? v : fallback);
const uniq = (arr, allowed) => (Array.isArray(arr) ? arr : []).filter((v, i, a) => allowed.includes(v) && a.indexOf(v) === i);

export const useOrdersUiStore = create((set) => ({
  ordersView: DEFAULT_ORDERS_VIEW,
  setOrdersView: (v) =>
    set({ ordersView: ORDERS_VIEWS.includes(v) ? v : DEFAULT_ORDERS_VIEW }),

  world: null,                                   // null · 'book' · 'desk' · 'collection'
  setWorld: (w) => set({ world: ORDERS_WORLDS.includes(w) ? w : null }),

  // B2a · the Desk's view + the order it is showing. deskOrderId is only
  // meaningful while deskView === 'card'; leaving the card clears it so a stale
  // id can never outlive the screen that used it.
  // deskCardTab (B4-2): the tab the card opens on — the list's PI-to-review
  // view opens Compare. Read once by the card as its initial tab; cleared with
  // the id, so it can never outlive the card it was meant for.
  deskView: DEFAULT_DESK_VIEW,
  deskOrderId: null,
  deskCardTab: null,
  // B6 · deskSupplier: the supplier the Suppliers view shows (mirrored as
  // &supplier=). deskListSupplier: a one-shot initial filter for the Orders
  // list, set by "Open in the desk" and cleared on the next view change.
  deskSupplier: null,
  // CLB-115 S4d · deskModel: the CL model the model page shows (mirrored as &model= while view=model).
  deskModel: null,
  deskListSupplier: null,
  deskCardsOpened: [],
  setDeskView: (v) => set((st) => ({
    deskView: DESK_VIEWS.includes(v) ? v : DEFAULT_DESK_VIEW,
    deskOrderId: v === 'card' ? st.deskOrderId : null,
    deskCardTab: v === 'card' ? st.deskCardTab : null,
    deskBatchId: v === 'batch' ? st.deskBatchId : null,
    deskSheetId: v === 'batch' ? st.deskSheetId : null,
    deskListSupplier: null,
    deskModel: v === 'model' ? st.deskModel : null,
  })),
  // B2b · the pile open in the batch workspace (&id=) and the sheet on the desk (&sheet=).
  deskBatchId: null,
  deskSheetId: null,
  openDeskBatch: (id, sheet) => set({ world: 'desk', deskView: 'batch', deskBatchId: id || null, deskSheetId: sheet || null, deskOrderId: null, deskCardTab: null, deskListSupplier: null }),
  setDeskSheet: (id) => set({ deskSheetId: id || null }),
  // the SAME card opened again with no tab (Ctrl K, its register row) keeps the tab it is on — the card does not remount, so
  // clearing it would drop ?pane=pi from the address while the screen stays on the PI stage (CLB-115 S3)
  openDeskOrder: (id, tab) => set((st) => ({ world: 'desk', deskView: 'card', deskOrderId: id || null,
    deskCardTab: typeof tab === 'string' ? tab : (id && st.deskView === 'card' && st.deskOrderId === id ? st.deskCardTab : null), deskListSupplier: null,
    // v12 (CLB-115 S2b) · the cards opened in this visit, newest first — the card's rail lists them; memory only
    deskCardsOpened: id ? [id, ...st.deskCardsOpened.filter((x) => x !== id)].slice(0, 8) : st.deskCardsOpened })),
  setDeskSupplier: (code) => set({ deskSupplier: typeof code === 'string' && code ? code : null }),
  openDeskModel: (cl) => set({ world: 'desk', deskView: 'model', deskModel: typeof cl === 'string' && cl ? cl : null, deskOrderId: null, deskCardTab: null, deskListSupplier: null }),
  // CLB-115 S3: the card's PI stage is an address (?…&pane=pi); the card writes it here as its tab changes
  setDeskCardTab: (tab) => set({ deskCardTab: tab || null }),
  openDeskOrders: (supplierCode) => set({ world: 'desk', deskView: 'orders', deskOrderId: null, deskCardTab: null, deskListSupplier: supplierCode || null }),

  // C4 · the Collection world. Everything the toolbar and the address share.
  // C8 · the Wall's deck picks: fam (metal · acetate) · mats (that family's materials) · subs ('parent|sub') · nyo.
  collection: { view: DEFAULT_COLLECTION_VIEW, model: null, q: '', status: [], group: 'none', sort: 'pieces', density: readDensity(), facets: EMPTY_FACETS,
    rq: '', rstatus: 'all', rsort: 'ordered', rdir: -1, rdensity: readDensity(RDENSITY_KEY), rfacets: EMPTY_RFACETS,
    xb: DEFAULT_EXCEPTION_BUCKET, xq: '', kind: 'all', closed: { rep: false, new: false, nyo: false },
    fam: '', mats: [], subs: [], nyo: false },
  setCollection: (patch) => set((st) => {
    const c = st.collection, p = patch || {};
    const next = {
      view: 'view' in p ? pick(COLLECTION_VIEWS, p.view, DEFAULT_COLLECTION_VIEW) : c.view,
      model: 'model' in p ? (typeof p.model === 'string' && p.model ? p.model : null) : c.model,
      q: 'q' in p ? String(p.q || '') : c.q,
      status: 'status' in p ? uniq(p.status, COLLECTION_STATUSES) : c.status,
      group: 'group' in p ? pick(COLLECTION_GROUPS, p.group, 'none') : c.group,
      sort: 'sort' in p ? pick(COLLECTION_SORTS, p.sort, 'pieces') : c.sort,
      density: 'density' in p ? pick(COLLECTION_DENSITIES, p.density, 'compact') : c.density,
      facets: 'facets' in p ? { ...EMPTY_FACETS, ...Object.fromEntries(Object.entries(p.facets || {}).filter(([k]) => COLLECTION_FACETS.includes(k)).map(([k, v]) => [k, Array.isArray(v) ? v.map(String) : []])) } : c.facets,
      rq: 'rq' in p ? String(p.rq || '') : c.rq,
      rstatus: 'rstatus' in p ? pick(REPEAT_STATUSES, p.rstatus, 'all') : c.rstatus,
      rsort: 'rsort' in p ? pick(REPEAT_SORTS, p.rsort, 'ordered') : c.rsort,
      rdir: 'rdir' in p ? (Number(p.rdir) > 0 ? 1 : -1) : c.rdir,
      rdensity: 'rdensity' in p ? pick(COLLECTION_DENSITIES, p.rdensity, 'compact') : c.rdensity,
      kind: 'kind' in p ? pick(WALL_KIND_KEYS, p.kind, 'all') : c.kind,
      closed: 'closed' in p ? { rep: !!p.closed?.rep, new: !!p.closed?.new, nyo: !!p.closed?.nyo } : c.closed,   // per section, remembered here, never on the address
      nyo: 'nyo' in p ? !!p.nyo : c.nyo,
      xb: 'xb' in p ? pick(EXCEPTION_BUCKETS, p.xb, DEFAULT_EXCEPTION_BUCKET) : c.xb,
      xq: 'xq' in p ? String(p.xq || '') : c.xq,
      rfacets: 'rfacets' in p ? { ...EMPTY_RFACETS, ...Object.fromEntries(Object.entries(p.rfacets || {}).filter(([k]) => REPEAT_FACETS.includes(k)).map(([k, v]) => [k, Array.isArray(v) ? v.map(String) : []])) } : c.rfacets,
    };
    // C8 · a new family drops the old family's materials unless the patch names them (collectionFacets.cleanPicks)
    const fam = 'fam' in p ? p.fam : c.fam;
    const picks = cleanPicks({ fam, mats: 'mats' in p ? p.mats : (fam === c.fam ? c.mats : []), subs: 'subs' in p ? p.subs : c.subs });
    const keep = (a, b) => (a.length === b.length && a.every((v, i) => v === b[i]) ? b : a);   // an unchanged list keeps its reference (memo deps)
    Object.assign(next, { fam: picks.fam, mats: keep(picks.mats, c.mats), subs: keep(picks.subs, c.subs) });
    if (next.density !== c.density) rememberDensity(next.density);
    if (next.rdensity !== c.rdensity) rememberDensity(next.rdensity, RDENSITY_KEY);
    return { collection: next };
  }),

  bookYear: null,                                // null = latest year with rows · 'all' · '2027'
  bookSub: 'all',
  bookKind: 'sun',                               // B1.5 · 'sun' | 'optic' — the third rail axis
  setBookYear: (y) => set({ bookYear: y == null ? null : String(y) }),
  setBookSub: (s) => set({ bookSub: BOOK_SUBS.includes(s) ? s : 'all' }),
  setBookKind: (b) => set({ bookKind: BOOK_KINDS.includes(b) ? b : 'sun' }),

  pendingLine: null,                             // orders_lines id awaiting the Table
  pendingComment: null,                          // orders_comments id, optional
  openLine: (lineId, commentId) => set({
    world: 'book', ordersView: 'data',
    pendingLine: lineId || null, pendingComment: commentId || null,
  }),
  clearPendingLine: () => set({ pendingLine: null, pendingComment: null }),
}));
